import './bootstrap';

import { createApp, h } from 'vue';
import { createInertiaApp, Link, http } from '@inertiajs/vue3';
import { DesignSystemLinkKey } from '@/design-system/lib/link';
import { DesignSystemLocaleKey } from '@/design-system/lib/locale';
import { APP_LOCALE } from '@/config/locale';
import * as Sentry from "@sentry/vue";
import moment from 'moment';
import can from './Plugins/can.js'; 
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { startActivityTracking } from './services/activityTracking';
import { registerToastFlashListener } from '@/Modules/Toast/Public';
import '../css/app.css';


import PrimeVue from 'primevue/config';
import Tooltip from 'primevue/tooltip';
import Aura from '@primevue/themes/aura';

// We need to determine the actual environment by looking at the domain


const isProduction = window.location.hostname === 'app.prowi.io';
const isStaging = window.location.hostname === 'prowi-app-staging.herokuapp.com';

// 'staging'|'production'|'local'
const environment = isProduction ? 'production'
                 : isStaging ? 'staging'
                 : 'local'; // default to local if none match


window.addEventListener('vite:preloadError', (event) => {
    console.error('Vite preload error:', event.detail);
    window.location.reload() // for example, refresh the page
});

// Inertia 3.x uses its own XHR client (not axios), so it needs the XSRF cookie
// name set here — the axios default in bootstrap.js does not reach it. Must match
// what the backend writes; parallel localhost devcontainers port-suffix it (see
// devtools/setup/configure-ports.sh). Three name-sources stay in sync: backend
// config/session.php (SESSION_XSRF_COOKIE), this Inertia client, and the axios
// default in bootstrap.js (both frontend sides read VITE_XSRF_COOKIE).
http.setClient({
    xsrfCookieName: import.meta.env.VITE_XSRF_COOKIE || 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',
});

// One global listener for server toasts (Inertia flash); see Modules/Toast.
registerToastFlashListener();

export default createInertiaApp({
    title: (title) => `${title} - Prowi`,
    resolve: (name) => {
        const modulePages = import.meta.glob('./Modules/**/Pages/**/*.vue');
        if (name.startsWith('Modules/')) {
            return resolvePageComponent(`./${name}.vue`, modulePages);
        }
        return resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue'));
    },
    progress: {
        color: '#4B5563',
    },
    setup({ el, App, props, plugin }) {

        const VueApp =  createApp(
            {
                render: () => h(App, props)
            }
        )

        /**
         * Configure Sentry for error tracking
         */
        if (import.meta.env.VITE_SENTRY_DSN_PUBLIC) {
          Sentry.init({
            app: VueApp,
            dsn: import.meta.env.VITE_SENTRY_DSN_PUBLIC,
            environment: environment,
            debug: environment !== 'production',

            integrations: [
              Sentry.replayIntegration(),
              Sentry.browserTracingIntegration(),
              Sentry.httpClientIntegration(),
              Sentry.reportingObserverIntegration(),
              Sentry.extraErrorDataIntegration(),
              Sentry.captureConsoleIntegration({ levels: ['error', 'warn'] }),
            ],
            trackComponents: true,

            // Performance: 10% of frontend transactions
            // Frontend traces are lightweight (few spans per transaction)
            tracesSampleRate: 0.1,

            // Trace propagation to connect frontend traces to backend
            tracePropagationTargets: [
              "localhost",
              /^https?:\/\/[^/]+\//,
            ],

            // Session Replay: 10% of normal sessions, 100% of error sessions
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
          });
        } else {
          console.log('VITE_SENTRY_DSN_PUBLIC not found. Sentry will not be enabled.');
        }
        
        VueApp.config.globalProperties.$filters = {
            dateTimeFromNow(value){
                return moment.utc(value).local().fromNow();
            },
            dateTime(value) {
                return moment(value).utcOffset(0).calendar();
            },
            dateTimeFixed(value){
                return moment(value).utcOffset(0).format("DD.MM.YY");
            }, 
            formatCurrency(value, currency = 'DKK') {
                // Use ISO currency codes for all currencies for consistency
                const upperCurrency = currency?.toUpperCase() || 'DKK';

                // Format number with da-DK locale and add ISO currency code manually
                const formatter = new Intl.NumberFormat('da-DK', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                });
                return `${upperCurrency} ${formatter.format(value)}`;
            }, 
            largeNo(value){
                if (!value) return '0.00';
                const num = parseFloat(value);
                if (num >= 1000000000) {
                    return (num / 1000000000).toFixed(2) + 'B';
                }
                if (num >= 1000000) {
                    return (num / 1000000).toFixed(2) + 'M';
                }
                if (num >= 1000) {
                    return (num / 1000).toFixed(1).replace('.0', '') + 'K'
                }
                return num.toFixed(2); 
            }   
        }
                    
        /**
         * PrimeVue Configuration
         */

        VueApp.use(
          PrimeVue, {
            // Default theme configuration
            theme: {
                preset: Aura,
                options: {
                    prefix: 'p',
                    darkModeSelector: '.dark-mode',
                    cssLayer: false
                }
            }
         }
        );
        VueApp.directive('tooltip', Tooltip);

        // Design system stays framework-agnostic: it injects its SPA-navigation
        // link rather than importing Inertia. Provide the real <Link> here, at the
        // composition root. See resources/js/design-system/lib/link.ts.
        VueApp.provide(DesignSystemLinkKey, Link);

        // Same idea for locale: the design system formats numbers/dates with the
        // app's locale (da-DK today, tenant-dynamic later) rather than importing
        // config. See resources/js/design-system/lib/locale.ts.
        VueApp.provide(DesignSystemLocaleKey, APP_LOCALE);


        VueApp.use(plugin)
            .mixin({ methods: { route, can } })
            .mount(el);

        // Report this tab's current page over Reverb presence (see
        // services/activityTracking.ts). Drives `php artisan activity:current`.
        startActivityTracking();


        return VueApp;
    },
});
